import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, ArrowRight, MessageSquare } from "lucide-react";
import { API, COMPANY } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const services = [
  { value: "haekklipning", label: "Hækklipning" },
  { value: "graes", label: "Græsslåning" },
  { value: "beskaering", label: "Beskæring / fældning" },
  { value: "havepleje", label: "Fast havepleje" },
  { value: "anlaeg", label: "Anlægsopgave" },
  { value: "andet", label: "Andet" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Tak for din besked! Vi vender tilbage hurtigst muligt.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Noget gik galt. Prøv venligst igen eller ring til os.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-4">Kontakt</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-brand-text text-balance">
            Lad os få en snak om <span className="italic text-brand-primary">din have</span>.
          </h1>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            Send os en besked, ring eller skriv en mail — vi svarer inden for 24 timer.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8">
          {/* Info column */}
          <div className="space-y-4">
            <div className="rounded-3xl bg-brand-primary text-brand-bg p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-bg/70">Ring til os</div>
              <a
                href={`tel:${COMPANY.phone}`}
                data-testid="contact-phone-link"
                className="mt-3 font-display text-4xl block hover:opacity-90"
              >
                {COMPANY.phoneDisplay}
              </a>
              <div className="flex items-center gap-2 text-sm text-brand-bg/80 mt-3">
                <Clock className="h-4 w-4" /> {COMPANY.hours}
              </div>
            </div>

            <div className="rounded-3xl bg-brand-surface border border-brand-border p-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-muted">E-mail</div>
                    <a href={`mailto:${COMPANY.email}`} className="text-brand-text hover:text-brand-primary" data-testid="contact-email-link">
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-muted">Område</div>
                    <div className="text-brand-text">{COMPANY.areas}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-muted">Svartid</div>
                    <div className="text-brand-text">Inden for 24 timer</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden border border-brand-border aspect-[4/3]">
              <iframe
                title="Kort"
                src="https://www.google.com/maps?q=Kolding,Denmark&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl bg-brand-surface border border-brand-border p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name" className="text-sm">Navn *</Label>
                <Input
                  id="c-name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1.5 h-12 bg-brand-bg border-brand-border"
                />
              </div>
              <div>
                <Label htmlFor="c-phone" className="text-sm">Telefon</Label>
                <Input
                  id="c-phone"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 h-12 bg-brand-bg border-brand-border"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="c-email" className="text-sm">E-mail *</Label>
              <Input
                id="c-email"
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5 h-12 bg-brand-bg border-brand-border"
              />
            </div>

            <div>
              <Label className="text-sm">Hvilken ydelse er du interesseret i?</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-service" className="mt-1.5 h-12 bg-brand-bg border-brand-border">
                  <SelectValue placeholder="Vælg ydelse" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="c-msg" className="text-sm">Besked *</Label>
              <Textarea
                id="c-msg"
                data-testid="contact-message"
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                placeholder="Fortæl kort om opgaven — fx hækkens længde, højde og hvornår det skal udføres."
                className="mt-1.5 bg-brand-bg border-brand-border"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-7 py-4 font-medium hover:bg-brand-hover disabled:opacity-60 transition-transform hover:-translate-y-0.5"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sender...</>
              ) : (
                <>Send besked <ArrowRight className="h-4 w-4" /></>
              )}
            </button>

            <p className="text-xs text-brand-muted">
              Ved at sende accepterer du, at vi kontakter dig ang. din henvendelse. Vi deler aldrig dine oplysninger med tredjepart.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
